import { FormikValues } from 'formik';
import { GenericObject, constValues, formatDate, getApplicationId, getIsSalesForce } from '@fnol/library/src/lib';
import { bankType, riskTypeLife, translations, transferTypeValues, submitterRole } from './consts';

export const isCountrySerbia = (countryId: string | number): boolean => {
    return countryId === constValues.countrySerbiaId.toString(10) || countryId === constValues.countrySerbiaId;
};

export const getCurrentDate = () => {
    const today = new Date();
    const day = today.getDate();
    const month = today.getMonth() + 1;
    const year = today.getFullYear();

    const singleDigit = (dateValue: number) => {
        if (dateValue < 10) {
            return '0' + dateValue;
        } else {
            return dateValue;
        }
    };

    const currentDate = `${singleDigit(day)}.${singleDigit(month)}.${year}. `;
    return currentDate;
};

interface IProps {
    id: string;
    documents: object;
}

export const prepareDocumentUploadFormData = ({ id, documents }: IProps) => {
    const formData = new FormData();
    const files = Object.values(documents)
        .filter((f) => f !== '')
        .map((f) => {
            const fileForPrepare = f.value;
            return new File([fileForPrepare], `${fileForPrepare.lastModified}@#$${fileForPrepare.name}`, {
                type: fileForPrepare.type,
            });
        });

    const dmsDocumentTypes = Object.values(documents).reduce((acc: GenericObject, f: GenericObject) => {
        const fileForPrepare = f.value;
        return {
            ...acc,
            [`${fileForPrepare.lastModified}@#$${fileForPrepare.name}`]: f.documentId,
        };
    }, {});

    files?.forEach((file: File) => {
        formData.append('files', new File([file], file.name, { type: file.type }));
    });

    formData.append('dmsDocumentTypes', JSON.stringify(dmsDocumentTypes));
    formData.append('stetaTempId', id);
    return formData;
};

export const prepareInsuredTypesList = (insuredTypes: { id: number; naziv: string }[] = []) => {
    return insuredTypes.map((insuredType) => {
        return {
            id: insuredType.id,
            value: insuredType.naziv,
        };
    });
};

export const prepareCitiesList = (cities: { id: number; naziv: string; sifra: string; postanskiBroj: string }[]) => {
    return cities.map((city) => {
        return {
            id: city.id,
            value: `${city.id}/${city.naziv}`,
            label: city.naziv,
            zipCode: city.postanskiBroj,
            code: city.sifra,
        };
    });
};

export const prepareRisksList = (risks: { grupa: string; id: number; naziv: string; napomena: string }[] = []) => {
    return risks.map((risk) => {
        return {
            id: risk.id,
            text: risk.naziv,
            value: risk.id,
            group: risk.grupa,
            note: risk.napomena,
        };
    });
};

/**
 * Prepare Formik data for submit
 *
 * @param values - object containing Formik state
 * @returns dataForSubmit - object with mapped data for submit
 */
export const prepareDataForSubmit = (values: FormikValues) => {
    const dataForSubmit: GenericObject = {};
    dataForSubmit.brojPolise = values.activePolicyNumber;
    dataForSubmit.datumNastanka = formatDate(values.dateOfOccurance);
    dataForSubmit.riziciId = values.risks;
    dataForSubmit.osiguranikId = values.activePolicy.osiguranikId;
    const firstCondition =
        values.risks.includes(riskTypeLife.injury) ||
        values.risks.includes(riskTypeLife.hospitalDaysDueToInjury) ||
        values.risks.includes(riskTypeLife.surgicalInterventionDueToInjury);
    const secondCondition =
        values.risks.includes(riskTypeLife.deathByAccident) ||
        values.risks.includes(riskTypeLife.deathByTrafficAccident);
    if (firstCondition) {
        dataForSubmit.opisSlucaja = values.description;
        dataForSubmit.zdravstvenaUstanova = values.firstAid;
        dataForSubmit.istraga = values.investigation;
        dataForSubmit.telesneMane = values.physicalFlaw;
        dataForSubmit.ranijePovrede = values.sameInjury;
        dataForSubmit.bolnickiDaniOd = formatDate(values.hospitalDaysStart);
        dataForSubmit.bolnickiDaniDo = formatDate(values.hospitalDaysEnd);
        dataForSubmit.dejstvoAlkoholaDroga = values.prohibitedSubstances;
        dataForSubmit.vrstaPovredeDijagnoza = values.typeOfInjury;
        dataForSubmit.hiruskaIntervencija = values.surgicalInvestigation;
    }
    if (secondCondition) {
        dataForSubmit.opisSlucaja = values.description;
        dataForSubmit.istraga = values.investigation;
        dataForSubmit.zdravstvenaUstanova = values.firstAid;
        dataForSubmit.dejstvoAlkoholaDroga = values.prohibitedSubstances;
    }
    if (values.risks.includes(riskTypeLife.hospitalDaysDueToIllness)) {
        dataForSubmit.vrstaPovredeDijagnoza = values.cause;
        dataForSubmit.bolnickiDaniOd = formatDate(values.hospitalDaysStart);
        dataForSubmit.bolnickiDaniDo = formatDate(values.hospitalDaysEnd);
        dataForSubmit.zdravstvenaUstanova = values.hospitalType;
        dataForSubmit.simptomiOd = formatDate(values.firstContactDate);
        dataForSubmit.zdravstvenaUstanovaLecenje = values.medicalRecord;
    }
    if (values.risks.includes(riskTypeLife.surgicalInterventionDueToIllness)) {
        dataForSubmit.vrstaPovredeDijagnoza = values.diagnosis;
        dataForSubmit.hiruskaIntervencija = values.surgicalIntervention;
        dataForSubmit.zdravstvenaUstanova = values.hospitalOfSurgical;
        dataForSubmit.simptomiOd = formatDate(values.dateOfFirstContact);
        dataForSubmit.zdravstvenaUstanovaLecenje = values.medicalRecord;
    }
    if (values.risks.includes(riskTypeLife.death)) {
        dataForSubmit.opisSlucaja = values.deathCause;
        dataForSubmit.zdravstvenaUstanova = values.drName;
        dataForSubmit.zdravstvenaUstanovaLecenje = values.institutionName;
    }
    if (values.risks.includes(riskTypeLife.seriousIllness)) {
        dataForSubmit.opisSlucaja = values.dateOfSeriousIllness;
        dataForSubmit.vrstaPovredeDijagnoza = values.diagnosisSeriousIllness;
        dataForSubmit.zdravstvenaUstanova = values.seriousIllnessTreatment;
        dataForSubmit.zdravstvenaUstanovaLecenje = values.medicalRecord;
    }
    /*
     *  Submitter data
     */
    {
        !(values.risks.includes(riskTypeLife.purchase) && values.submitterRole === submitterRole.contractor) && // ne salje se podnosilac ako je rizik otkup, a podnosilac je ugovarac
            (dataForSubmit.podnosilac = {
                uloga: values.submitterRole ? values.submitterRole : translations.personalInfo.DEFAULT_SUBMITTER,
                ime: values.submitterName,
                prezime: values.submitterLastName,
                email: values.submitterEmail,
                mobilniTelefon: values.submitterPhone,
            });
    }

    {
        !values.risks.includes(riskTypeLife.purchase) && (dataForSubmit.podnosilac.korisnik = values.isSubmitterUser);
    }

    /*
     *  Additional submitter data if submitter is also a user
     */
    if (
        values.isSubmitterUser &&
        values.isSubmitterUser === 'true' &&
        !(getIsSalesForce(getApplicationId()) && values.risks.includes(riskTypeLife.outlive))
    ) {
        dataForSubmit.podnosilac.pol = values.submitterGender;
        dataForSubmit.podnosilac.drzavljanstvoId = values.submitterCitizenship;
        dataForSubmit.podnosilac.drzavaId = values.submitterCountry;
        dataForSubmit.podnosilac.datumRodjenja = formatDate(values.submitterDateOfBirth);
        dataForSubmit.podnosilac.mestoRodjenja = values.submitterPlaceOfBirth;
        dataForSubmit.podnosilac.adresa = {
            ulicaBroj: values.submitterStreet,
            opstina: values.submitterMunicipality,
            postanskiBroj: values.submitterZipCode,
        };
        if (
            values.submitterCountry !== constValues.countrySerbiaId.toString(10) &&
            values.submitterCountry !== constValues.countrySerbiaId
        ) {
            dataForSubmit.podnosilac.adresa.mesto = values.submitterCity;
            dataForSubmit.podnosilac.jmbg = '1111111111111';
        } else {
            dataForSubmit.podnosilac.adresa.mesto = values.submitterCityInSerbia.label;
            dataForSubmit.podnosilac.adresa.mestoId = values.submitterCityInSerbia.id;
            dataForSubmit.podnosilac.jmbg = values.submitterUCN;
        }
        if (values.submitterCurrentResidenceAddress === 'false') {
            dataForSubmit.podnosilac.adresaBoravista = {
                ulicaBroj: values.submitterCurrentResidenceStreet,
                opstina: values.submitterCurrentResidenceMunicipality,
                postanskiBroj: values.submitterCurrentResidenceZipCode,
            };
            if (
                values.submitterCountry !== constValues.countrySerbiaId.toString(10) &&
                values.submitterCountry !== constValues.countrySerbiaId
            ) {
                dataForSubmit.podnosilac.adresaBoravista.mesto = values.submitterCurrentResidenceCity;
            } else {
                dataForSubmit.podnosilac.adresaBoravista.mesto = values.submitterCurrentResidenceCityInSerbia.label;
                dataForSubmit.podnosilac.adresaBoravista.mestoId = values.submitterCurrentResidenceCityInSerbia.id;
            }
        }
        if (values.submitterMailingAddress === 'false') {
            dataForSubmit.podnosilac.adresaSlanjePoste = {
                ulicaBroj: values.submitterMailingStreet,
                opstina: values.submitterMailingMunicipality,
                postanskiBroj: values.submitterMailingZipCode,
            };
            if (
                values.submitterCountry !== constValues.countrySerbiaId.toString(10) &&
                values.submitterCountry !== constValues.countrySerbiaId
            ) {
                dataForSubmit.podnosilac.adresaSlanjePoste.mesto = values.submitterMailingCity;
            } else {
                dataForSubmit.podnosilac.adresaSlanjePoste.mesto = values.submitterMailingCityInSerbia.label;
                dataForSubmit.podnosilac.adresaSlanjePoste.mestoId = values.submitterMailingCityInSerbia.id;
            }
        }
        dataForSubmit.podnosilac.korisnikDetalji = {
            licniDokument: values.submitterPersonalDocumentType,
            brojLicnogDokumenta: values.submitterPersonalDocumentNumber,
            mestoIzdavanjaLicnogDokumenta: values.submitterDocumentIssuePlace,
            datumIzdavanjaLicnogDokumenta: formatDate(values.submitterDocumentIssueDate),
            izjavaStvarniVlasnik: values.submitterStatement,
        };
        if (values.submitterStatement === 'false') {
            dataForSubmit.podnosilac.korisnikDetalji.izjavaStvarniVlasnikNapomena = values.submitterStatementNote;
        }
        if (getIsSalesForce(getApplicationId())) {
            dataForSubmit.podnosilac.korisnikDetalji.potpisanAML = values.submitterAttorneySignature;
            if (values.isSubmitterUser === 'true') {
                dataForSubmit.podnosilac.korisnikDetalji.punomocnikKorisnika = values.isSubmitterUsersAttorney;
            }
        }
        if (values.userAccountNumber && values.userAccountNumber !== '') {
            dataForSubmit.podnosilac.placanje = {
                tekuciRacun: values.userAccountNumber,
                nazivBanke: values.userBankName,
                domacaBanka: values.userBank === bankType.domestic || values.userBank === bankType.iban,
            };
        }
        if (
            (values.risks.includes(riskTypeLife.outlive) || values.risks.includes(riskTypeLife.purchase)) &&
            values.outliveFullPayment === 'false' &&
            values.outliveTransfer === 'true'
        ) {
            dataForSubmit.podnosilac.placanje = {
                ...dataForSubmit.podnosilac.placanje,
                placanje: values.transferType,
                brojPonude: values.transferOffer1Number,
            };

            if (values.transferOffer2Number !== '') {
                dataForSubmit.podnosilac.placanje.iznosZaIsplatu = values.transfer1Amount;
                dataForSubmit.podnosilac.placanje.brojDrugePonude = values.transferOffer2Number;
                dataForSubmit.podnosilac.placanje.iznosZaIsplatuDrugaPonuda = values.transfer2Amount;
            }
        }
        /*
         *  Additional third person data if submitter is also a user with payment to third person
         */
        if (values.thirdPersonName && values.thirdPersonName !== '') {
            dataForSubmit.podnosilac.treceLice = {
                ime: values.thirdPersonName,
                prezime: values.thirdPersonLastName,
                email: values.thirdPersonEmail,
                mobilniTelefon: values.thirdPersonPhone,
                pol: values.thirdPersonGender,
                drzavljanstvoId: values.thirdPersonCitizenship,
                drzavaId: values.thirdPersonCountry,
                datumRodjenja: formatDate(values.thirdPersonDateOfBirth),
                mestoRodjenja: values.thirdPersonPlaceOfBirth,
                adresa: {
                    ulicaBroj: values.thirdPersonStreet,
                    opstina: values.thirdPersonMunicipality,
                    postanskiBroj: values.thirdPersonZipCode,
                },
                korisnikDetalji: {
                    licniDokument: values.thirdPersonPersonalDocumentType,
                    brojLicnogDokumenta: values.thirdPersonPersonalDocumentNumber,
                    mestoIzdavanjaLicnogDokumenta: values.thirdPersonDocumentIssuePlace,
                    datumIzdavanjaLicnogDokumenta: formatDate(values.thirdPersonDocumentIssueDate),
                    izjavaStvarniVlasnik: values.thirdPersonStatement,
                },
            };
            if (values.thirdPersonStatement === 'false') {
                dataForSubmit.podnosilac.treceLice.korisnikDetalji.izjavaStvarniVlasnikNapomena =
                    values.thirdPersonStatementNote;
            }
            if (
                values.thirdPersonCountry !== constValues.countrySerbiaId.toString(10) &&
                values.thirdPersonCountry !== constValues.countrySerbiaId
            ) {
                dataForSubmit.podnosilac.treceLice.adresa.mesto = values.thirdPersonCity;
                dataForSubmit.podnosilac.treceLice.jmbg = '1111111111111';
            } else {
                dataForSubmit.podnosilac.treceLice.adresa.mesto = values.thirdPersonCityInSerbia.label;
                dataForSubmit.podnosilac.treceLice.adresa.mestoId = values.thirdPersonCityInSerbia.id;
                dataForSubmit.podnosilac.treceLice.jmbg = values.thirdPersonUCN;
            }
            if (values.thirdPersonCurrentResidenceAddress === 'false') {
                dataForSubmit.podnosilac.treceLice.adresaBoravista = {
                    ulicaBroj: values.thirdPersonCurrentResidenceStreet,
                    opstina: values.thirdPersonCurrentResidenceMunicipality,
                    postanskiBroj: values.thirdPersonCurrentResidenceZipCode,
                };
                if (
                    values.thirdPersonCountry !== constValues.countrySerbiaId.toString(10) &&
                    values.thirdPersonCountry !== constValues.countrySerbiaId
                ) {
                    dataForSubmit.podnosilac.treceLice.adresaBoravista.mesto = values.thirdPersonCurrentResidenceCity;
                } else {
                    dataForSubmit.podnosilac.treceLice.adresaBoravista.mesto =
                        values.thirdPersonCurrentResidenceCityInSerbia.label;
                    dataForSubmit.podnosilac.treceLice.adresaBoravista.mestoId =
                        values.thirdPersonCurrentResidenceCityInSerbia.id;
                }
            }
            if (values.thirdPersonMailingAddress === 'false') {
                dataForSubmit.podnosilac.treceLice.adresaSlanjePoste = {
                    ulicaBroj: values.thirdPersonMailingStreet,
                    opstina: values.thirdPersonMailingMunicipality,
                    postanskiBroj: values.thirdPersonMailingZipCode,
                };
                if (
                    values.thirdPersonCountry !== constValues.countrySerbiaId.toString(10) &&
                    values.thirdPersonCountry !== constValues.countrySerbiaId
                ) {
                    dataForSubmit.podnosilac.treceLice.adresaSlanjePoste.mesto = values.thirdPersonMailingCity;
                } else {
                    dataForSubmit.podnosilac.treceLice.adresaSlanjePoste.mesto =
                        values.thirdPersonMailingCityInSerbia.label;
                    dataForSubmit.podnosilac.treceLice.adresaSlanjePoste.mestoId =
                        values.thirdPersonMailingCityInSerbia.id;
                }
            }
            if (values.thirdPersonAccountNumber && values.thirdPersonAccountNumber !== '') {
                dataForSubmit.podnosilac.treceLice.placanje = {
                    tekuciRacun: values.thirdPersonAccountNumber,
                    nazivBanke: values.thirdPersonBankName,
                    domacaBanka:
                        values.thirdPersonBank === bankType.domestic || values.thirdPersonBank === bankType.iban,
                };
            }
            if (
                (values.risks.includes(riskTypeLife.outlive) || values.risks.includes(riskTypeLife.purchase)) &&
                values.outliveFullPayment === 'false' &&
                values.outliveTransfer === 'true'
            ) {
                dataForSubmit.podnosilac.treceLice.placanje = {
                    ...dataForSubmit.podnosilac.treceLice.placanje,
                    placanje: values.transferType,
                    brojPonude: values.transferOffer1Number,
                    iznosZaIsplatu: values.transfer1Amount,
                };

                if (values.transferOffer2Number !== '') {
                    dataForSubmit.podnosilac.treceLice.placanje.brojDrugePonude = values.transferOffer2Number;
                    dataForSubmit.podnosilac.treceLice.placanje.iznosZaIsplatuDrugaPonuda = values.transfer2Amount;
                }
            }
        }
    }
    /*
     *  Users data
     */
    const userPersons: GenericObject[] = [];
    const userIds = ['', 1, 2, 3, 4, 5];
    userIds.map((userId) => {
        if (values[`user${userId}Name`]) {
            const userPerson: GenericObject = {
                ime: values[`user${userId}Name`],
                prezime: values[`user${userId}LastName`],
                drzavljanstvoId: values[`user${userId}Citizenship`],
                drzavaId: values[`user${userId}Country`],
                postanskiBroj: values[`user${userId}ZipCode`],
                datumRodjenja: formatDate(values[`user${userId}DateOfBirth`]),
                mestoRodjenja: values[`user${userId}PlaceOfBirth`],
                mobilniTelefon: values[`user${userId}Phone`],
                email: values[`user${userId}Email`],
            };
            userPerson.adresa = {
                ulicaBroj: values[`user${userId}Street`],
                opstina: values[`user${userId}Municipality`],
                postanskiBroj: values[`user${userId}ZipCode`],
            };
            if (
                values[`user${userId}Country`] !== constValues.countrySerbiaId.toString(10) &&
                values[`user${userId}Country`] !== constValues.countrySerbiaId
            ) {
                userPerson.adresa.mesto = values[`user${userId}City`];
                userPerson.jmbg = values.risks.includes(riskTypeLife.outlive)
                    ? values[`user${userId}UCN`]
                    : '1111111111111';
            } else {
                userPerson.adresa.mesto = values[`user${userId}CityInSerbia`].label;
                userPerson.adresa.mestoId = values[`user${userId}CityInSerbia`].id;
                userPerson.jmbg = values[`user${userId}UCN`];
            }
            if (values[`user${userId}CurrentResidenceAddress`] === 'false') {
                userPerson.adresaBoravista = {
                    ulicaBroj: values[`user${userId}CurrentResidenceStreet`],
                    opstina: values[`user${userId}CurrentResidenceMunicipality`],
                    postanskiBroj: values[`user${userId}CurrentResidenceZipCode`],
                };
                if (
                    values[`user${userId}Country`] !== constValues.countrySerbiaId.toString(10) &&
                    values[`user${userId}Country`] !== constValues.countrySerbiaId
                ) {
                    userPerson.adresaBoravista.mesto = values[`user${userId}CurrentResidenceCity`];
                } else {
                    userPerson.adresaBoravista.mesto = values[`user${userId}CurrentResidenceCityInSerbia`].label;
                    userPerson.adresaBoravista.mestoId = values[`user${userId}CurrentResidenceCityInSerbia`].id;
                }
            }
            if (values[`user${userId}MailingAddress`] === 'false') {
                userPerson.adresaSlanjePoste = {
                    ulicaBroj: values[`user${userId}MailingStreet`],
                    opstina: values[`user${userId}MailingMunicipality`],
                    postanskiBroj: values[`user${userId}MailingZipCode`],
                };
                if (
                    values[`user${userId}Country`] !== constValues.countrySerbiaId.toString(10) &&
                    values[`user${userId}Country`] !== constValues.countrySerbiaId
                ) {
                    userPerson.adresaSlanjePoste.mesto = values[`user${userId}MailingCity`];
                } else {
                    userPerson.adresaSlanjePoste.mesto = values[`user${userId}MailingCityInSerbia`].label;
                    userPerson.adresaSlanjePoste.mestoId = values[`user${userId}MailingCityInSerbia`].id;
                }
            }
            userPerson.korisnikDetalji = {
                licniDokument: values[`user${userId}PersonalDocumentType`],
                brojLicnogDokumenta: values[`user${userId}PersonalDocumentNumber`],
                mestoIzdavanjaLicnogDokumenta: values[`user${userId}DocumentIssuePlace`],
                datumIzdavanjaLicnogDokumenta: formatDate(values[`user${userId}DocumentIssueDate`]),
                izjavaStvarniVlasnik: values[`user${userId}Statement`],
            };

            if (values[`user${userId}Statement`] === 'false') {
                userPerson.korisnikDetalji.izjavaStvarniVlasnikNapomena = values[`user${userId}StatementNote`];
            }

            if (values[`user${userId}AccountNumber`] && values[`user${userId}AccountNumber`] !== '') {
                userPerson.placanje = {
                    tekuciRacun: values[`user${userId}AccountNumber`],
                    nazivBanke: values[`user${userId}BankName`],
                    domacaBanka:
                        values[`user${userId}Bank`] === bankType.domestic ||
                        values[`user${userId}Bank`] === bankType.iban,
                };
            }
            if (
                (values.risks.includes(riskTypeLife.outlive) || values.risks.includes(riskTypeLife.purchase)) &&
                values[`outliveFullPayment${userId}`] === 'false' &&
                values[`outliveTransfer${userId}`] === 'true'
            ) {
                userPerson.placanje = {
                    ...userPerson.placanje,
                    placanje: values[`transferType${userId}`],
                    brojPonude: values[`transferOffer1Number${userId}`],
                    iznosZaIsplatu: values[`transfer1Amount${userId}`],
                };

                if (values.transferOffer2Number !== '') {
                    userPerson.placanje.brojDrugePonude = values[`transferOffer2Number${userId}`];
                    userPerson.placanje.iznosZaIsplatuDrugaPonuda = values[`transfer2Amount${userId}`];
                }
                // if (values[`transferType${userId}`] === transferTypeValues.partial) {
                //     userPerson.placanje.iznosZaIsplatu = values[`transferAmount${userId}`];
                // }
            }
            if (getIsSalesForce(getApplicationId())) {
                userPerson.korisnikDetalji.potpisanAML = values[`user${userId}AttorneySignature`];
            }

            /*
             *  Third person data when payment should be payed to a third person instead of a user
             */
            if (values[`thirdPerson${userId}Name`] && values[`thirdPerson${userId}Name`] !== '') {
                userPerson.treceLice = {
                    ime: values[`thirdPerson${userId}Name`],
                    prezime: values[`thirdPerson${userId}LastName`],
                    email: values[`thirdPerson${userId}Email`],
                    mobilniTelefon: values[`thirdPerson${userId}Phone`],
                    pol: values[`thirdPerson${userId}Gender`],
                    drzavljanstvoId: values[`thirdPerson${userId}Citizenship`],
                    drzavaId: values[`thirdPerson${userId}Country`],
                    datumRodjenja: formatDate(values[`thirdPerson${userId}DateOfBirth`]),
                    mestoRodjenja: values[`thirdPerson${userId}PlaceOfBirth`],
                    adresa: {
                        ulicaBroj: values[`thirdPerson${userId}Street`],
                        opstina: values[`thirdPerson${userId}Municipality`],
                        postanskiBroj: values[`thirdPerson${userId}ZipCode`],
                    },
                    korisnikDetalji: {
                        licniDokument: values[`thirdPerson${userId}PersonalDocumentType`],
                        brojLicnogDokumenta: values[`thirdPerson${userId}PersonalDocumentNumber`],
                        mestoIzdavanjaLicnogDokumenta: values[`thirdPerson${userId}DocumentIssuePlace`],
                        datumIzdavanjaLicnogDokumenta: formatDate(values[`thirdPerson${userId}DocumentIssueDate`]),
                        izjavaStvarniVlasnik: values[`thirdPerson${userId}Statement`],
                    },
                };
                if (values[`thirdPerson${userId}Statement`] === 'false') {
                    userPerson.treceLice.korisnikDetalji.izjavaStvarniVlasnikNapomena =
                        values[`thirdPerson${userId}StatementNote`];
                }

                if (
                    values[`thirdPerson${userId}Country`] !== constValues.countrySerbiaId.toString(10) &&
                    values[`thirdPerson${userId}Country`] !== constValues.countrySerbiaId
                ) {
                    userPerson.treceLice.adresa.mesto = values[`thirdPerson${userId}City`];
                    userPerson.treceLice.jmbg = '1111111111111';
                } else {
                    userPerson.treceLice.adresa.mesto = values[`thirdPerson${userId}CityInSerbia`].label;
                    userPerson.treceLice.adresa.mestoId = values[`thirdPerson${userId}CityInSerbia`].id;
                    userPerson.treceLice.jmbg = values[`thirdPerson${userId}UCN`];
                }
                if (values[`thirdPerson${userId}CurrentResidenceAddress`] === 'false') {
                    userPerson.treceLice.adresaBoravista = {
                        ulicaBroj: values[`thirdPerson${userId}CurrentResidenceStreet`],
                        opstina: values[`thirdPerson${userId}CurrentResidenceMunicipality`],
                        postanskiBroj: values[`thirdPerson${userId}CurrentResidenceZipCode`],
                    };
                    if (
                        values[`thirdPerson${userId}Country`] !== constValues.countrySerbiaId.toString(10) &&
                        values[`thirdPerson${userId}Country`] !== constValues.countrySerbiaId
                    ) {
                        userPerson.treceLice.adresaBoravista.mesto = values[`thirdPerson${userId}CurrentResidenceCity`];
                    } else {
                        userPerson.treceLice.adresaBoravista.mesto =
                            values[`thirdPerson${userId}CurrentResidenceCityInSerbia`].label;
                        userPerson.treceLice.adresaBoravista.mestoId =
                            values[`thirdPerson${userId}CurrentResidenceCityInSerbia`].id;
                    }
                }
                if (values[`thirdPerson${userId}MailingAddress`] === 'false') {
                    userPerson.treceLice.adresaSlanjePoste = {
                        ulicaBroj: values[`thirdPerson${userId}MailingStreet`],
                        opstina: values[`thirdPerson${userId}MailingMunicipality`],
                        postanskiBroj: values[`thirdPerson${userId}MailingZipCode`],
                    };
                    if (
                        values[`thirdPerson${userId}Country`] !== constValues.countrySerbiaId.toString(10) &&
                        values[`thirdPerson${userId}Country`] !== constValues.countrySerbiaId
                    ) {
                        userPerson.treceLice.adresaSlanjePoste.mesto = values[`thirdPerson${userId}MailingCity`];
                    } else {
                        userPerson.treceLice.adresaSlanjePoste.mesto =
                            values[`thirdPerson${userId}MailingCityInSerbia`].label;
                        userPerson.treceLice.adresaSlanjePoste.mestoId =
                            values[`thirdPerson${userId}MailingCityInSerbia`].id;
                    }
                }

                if (values[`thirdPerson${userId}AccountNumber`] && values[`thirdPerson${userId}AccountNumber`] !== '') {
                    userPerson.treceLice.placanje = {
                        tekuciRacun: values[`thirdPerson${userId}AccountNumber`],
                        nazivBanke: values[`thirdPerson${userId}BankName`],
                        domacaBanka:
                            values[`thirdPerson${userId}Bank`] === bankType.domestic ||
                            values[`thirdPerson${userId}Bank`] === bankType.iban,
                    };
                }

                if (
                    (values.risks.includes(riskTypeLife.outlive) || values.risks.includes(riskTypeLife.purchase)) &&
                    values[`outliveFullPayment${userId}`] === 'false' &&
                    values[`outliveTransfer${userId}`] === 'true'
                ) {
                    userPerson.treceLice.placanje = {
                        ...userPerson.treceLice.placanje,
                        placanje: values[`transferType${userId}`],
                        brojPonude: values[`transferOffer1Number${userId}`],
                        iznosZaIsplatu: values[`transfer1Amount${userId}`],
                    };

                    if (values.transferOffer2Number !== '') {
                        userPerson.treceLice.placanje.brojDrugePonude = values.transferOffer2Number;
                        userPerson.treceLice.placanje.iznosZaIsplatuDrugaPonuda = values.transfer2Amount;
                    }

                    // if (values[`transferType${userId}`] === transferTypeValues.partial) {
                    //     userPerson.treceLice.placanje.iznosZaIsplatu = values[`transfer1Amount${userId}`];
                    // }
                }
            }
            userPersons.push(userPerson);
        }
    });
    const userLegalEntity: GenericObject = {
        naziv: values[`userBusinessName`],
        pib: values[`userVAT`].replace(/ /g, ''),
        drzavaId: values[`userCountry`],
        ulicaIBroj: values[`userStreet`],
        datumOsnivanja: formatDate(values[`userEstablishmentDate`]),
        mestoOsnivanja: values[`userEstablishmentPlace`],
        mesto: values[`userCityInSerbia`].label,
        opstina: values[`userMunicipality`],
        imeZakonskogZastupnika: values[`userName`],
        prezimeZakonskogZastupnika: values[`userLastName`],
        ptt: values[`userZipCode`],
        kontaktMobilniTelefon: values[`userPhone`],
        email: values[`userEmail`],
        licniDokumentZakonskogZastupnika: values[`userPersonalDocumentType`],
        brojLicnogDokumentaZakonskogZastupnika: values[`userPersonalDocumentNumber`],
        mestoIzdavanjaLicnogDokumentaZakonskogZastupnika: values[`userDocumentIssuePlace`],
        datumIzdavanjaLicnogDokumentaZakonskogZastupnika: formatDate(values[`userDocumentIssueDate`]),
        potpisanAML: values[`userAttorneySignature`],
    };
    if (values[`userAccountNumber`] && values[`userAccountNumber`] !== '') {
        userLegalEntity.placanje = {
            tekuciRacun: values[`userAccountNumber`],
            nazivBanke: values[`userBankName`],
            domacaBanka: values[`userBank`] === bankType.domestic || values[`userBank`] === bankType.iban,
            brojPonude: values[`transferOffer1Number`],
            iznosZaIsplatu: values[`transfer1Amount`],
        };

        if (values.transferOffer2Number !== '') {
            userLegalEntity.placanje.brojDrugePonude = values.transferOffer2Number;
            userLegalEntity.placanje.iznosZaIsplatuDrugaPonuda = values.transfer2Amount;
        }
    }
    if (userPersons.length > 0) {
        if (!(values.risks.includes(riskTypeLife.purchase) && values.userTypePurchase === 2)) {
            dataForSubmit.korisnici = userPersons;
        } else {
            dataForSubmit.korisnikPL = userLegalEntity;
        }
    }

    return dataForSubmit;
};
